import { isEBSEmbedded } from '@xmatters/common/context/ebsGlobalContext';
import memoize from 'lodash/memoize';

const APP_PATH_PREFIX = '/xmatters/ui';
const LEGACY_APP_PATH_PREFIX = '/xmatters/app.do';

export const isEmbedded = () => isEBSEmbedded(); // note: future proofing, if we end up embedding in other hosts
const isPathOutsideMainApp = path =>
  path &&
  path.indexOf(APP_PATH_PREFIX) === -1 &&
  path.indexOf(LEGACY_APP_PATH_PREFIX) === -1;

export const isToCocoon = to => to.indexOf('CocoonPlace') !== -1;
export const isFromCocoon = () =>
  !isEmbedded() && isPathOutsideMainApp(window.location.href);
export const isLeavingCocoon = to => isFromCocoon() && !isToCocoon(to);

export const isAppLoadedOnLegacyUrl = () =>
  !isEmbedded() && window.location.href.indexOf(LEGACY_APP_PATH_PREFIX) > -1;

const getPlaceRoute = place => {
  const prefix = isAppLoadedOnLegacyUrl() ? '#' : '/';
  return place.startsWith(prefix) ? place : prefix + place;
};

let embeddedAppPath = '';
export const setEmbeddedAppPath = appPath => {
  embeddedAppPath = appPath;
};
export const getEmbeddedAppPath = () => embeddedAppPath;
const getAppPath = () => {
  if (isEmbedded()) {
    return embeddedAppPath;
  }
  return isAppLoadedOnLegacyUrl() ? LEGACY_APP_PATH_PREFIX : APP_PATH_PREFIX;
};

export function getPlacePath(place) {
  if (isToCocoon(place)) {
    return place.replace('CocoonPlace:', '');
  }
  const route = getPlaceRoute(place);
  if (isLeavingCocoon(place) || !isAppLoadedOnLegacyUrl()) {
    const appPath = getAppPath();
    return place.startsWith(appPath) ? route : appPath + route;
  }
  return route;
}

export function getPlaceUrl(place) {
  return `${window.location.origin}${getPlacePath(place)}`;
}

export const cocoonPreventsNavigation = url =>
  window.navigateAway && !window.navigateAway(url);

function navigate(path) {
  if (cocoonPreventsNavigation(path)) {
    return Promise.resolve();
  }

  return new Promise(resolve =>
    // note: doing this async is necessary in order to let side effects
    // like setting the page dirty, etc. execute
    setTimeout(() => {
      if (isEmbedded() || isAppLoadedOnLegacyUrl() || isFromCocoon()) {
        window.location.href = path;
      } else {
        window.xmGWTHandleNavigation(path);
      }
      resolve();
    }, 0),
  );
}

export function goToPlace(navPlace) {
  return navigate(getPlacePath(navPlace));
}

/*
 * Generating URLs
 */
function buildGWTPlaceRouteRaw(placeStr) {
  return new RegExp(`^${getAppPath()}${getPlaceRoute(placeStr)}`);
}
export const buildGWTPlaceRoute = memoize(buildGWTPlaceRouteRaw);

function buildTopLevelRouteRaw(pathStart) {
  return new RegExp(`^/xmatters/${pathStart}`);
}
export const buildTopLevelRoute = memoize(buildTopLevelRouteRaw);

/*
 * Checking if a URL matches the current path
 */
export function getCurrentLocation() {
  return window.location.pathname + window.location.hash;
}

export const getCurrentPath = () => {
  let currentPath = getCurrentLocation();
  const appPath = getAppPath();
  if (currentPath.startsWith(appPath)) {
    currentPath = currentPath.replace(appPath, '');
  }
  if (currentPath.startsWith('/')) {
    currentPath = currentPath.replace('/', '');
  }
  if (currentPath.startsWith('#')) {
    currentPath = currentPath.replace('#', '');
  }
  return currentPath;
};

export function routeMatchesCurrentPath(item) {
  return item.test(getCurrentLocation());
}

const toLinkCocoonPrefix = /^CocoonPlace:/;

function getToLinkAsUrlRouteRaw(toLink) {
  if (toLinkCocoonPrefix.test(toLink)) {
    const pathStart = toLink.replace(toLinkCocoonPrefix, '');
    return new RegExp(`^${pathStart}`);
  }

  // Default case. TODO: consider ignoring params by default (split at `:`)
  return buildGWTPlaceRoute(toLink);
}
export const getToLinkAsUrlRoute = memoize(getToLinkAsUrlRouteRaw);

export function placeMatchesCurrentPath(toLink, exclude) {
  const currentLocation = getCurrentLocation();
  const containsExclusions = exclude.some(item =>
    currentLocation.includes(item),
  );
  return (
    getToLinkAsUrlRoute(toLink).test(getCurrentLocation()) &&
    !containsExclusions
  );
}

const prependSlashIfNecessary = path =>
  path.startsWith('/') ? path : `/${path}`;

export const getRouterPath = path =>
  isAppLoadedOnLegacyUrl() || isEmbedded()
    ? prependSlashIfNecessary(path)
    : APP_PATH_PREFIX + getPlaceRoute(path);

// Handle both "Nice URL" paths and legacy paths
export const getPathsForGwtPlace = memoize((place, opts = {}) => {
  const params = opts.params ?? '(.+)?';

  const capitalizedPlace = place.charAt(0).toUpperCase() + place.slice(1);
  if (isAppLoadedOnLegacyUrl()) {
    return [getRouterPath(`/${capitalizedPlace}Place(:?)${params}`)];
  }

  return [
    getRouterPath(`/${place}([:/]?)${params}`),
    getRouterPath(`/${capitalizedPlace}Place([:/]?)${params}`),
  ];
});

/**
 * Useful for converting old GWT paths to newer "Nice URL" paths.
 * example: page lands on `TrackingReportPlace:eventUuid=2dee6dc0-b3bb-4403-95da-cc7cd80ef178` => returns `trackingReport`
 */
export function getNicePlaceLocation() {
  // Handle nice URLs (//) and old URLs (//)
  let currentPlace = getCurrentPath();
  if (currentPlace.startsWith('/')) {
    currentPlace = currentPlace.replace('/', '');
  }
  // Remove args and any trailing paths:
  currentPlace = currentPlace.split(':')[0].split('/')[0];

  // Downcase first character if needed:
  currentPlace = currentPlace.charAt(0).toLowerCase() + currentPlace.slice(1);

  // Remove "Place" from the end:
  return currentPlace.replace(/Place$/, '');
}

export const isBlankRoute = () =>
  !isFromCocoon() &&
  (isAppLoadedOnLegacyUrl()
    ? !window.location.hash
    : /^\/xmatters\/ui\/?$/.test(window.location.pathname));
